import { Platform } from 'react-native';

// ─── Colors ──────────────────────────────────────────────────────────────────
// On WEB every token resolves to the matching CSS custom property in theme.css,
// so flipping `data-theme="dark"` on <html> recolors React-Native StyleSheet
// screens too (RNW passes any `var(...)` color through untouched). On NATIVE
// there are no CSS variables, so we fall back to the baked hex value — the same
// light-theme value as before. Dark mode is a web-only feature.
const c = (cssVar: string, hex: string): string =>
  Platform.OS === 'web' ? `var(${cssVar})` : hex;

export const Colors = {
  paper:        c('--paper',   '#F9F8F5'),  // oklch(0.985 0.003 90)
  paper2:       c('--paper-2', '#F4F2EC'),  // oklch(0.970 0.004 90)
  paper3:       c('--paper-3', '#EDE9E0'),  // oklch(0.955 0.005 90)
  card:         c('--card',    '#FFFFFF'),

  ink:          c('--ink',          '#1C1C2E'),  // oklch(0.20 0.010 250)
  ink2:         c('--ink-2',        '#3D3D55'),  // oklch(0.38 0.010 250)
  ink3:         c('--ink-3',        '#666680'),  // oklch(0.55 0.010 250)
  ink4:         c('--ink-4',        '#9999AA'),  // oklch(0.72 0.008 250)
  line:         c('--line',         '#E2E1DC'),  // oklch(0.92 0.004 250)
  lineStrong:   c('--line-strong',  '#CCCCC4'),  // oklch(0.85 0.005 250)

  accent:       c('--accent',       '#4F46E5'),  // oklch(0.52 0.16 260) — indigo
  accentInk:    c('--accent-ink',   '#312E81'),  // oklch(0.35 0.14 260)
  accentSoft:   c('--accent-soft',  '#EEF2FF'),  // oklch(0.96 0.03 260)
  accentLine:   c('--accent-line',  '#C7D2FE'),  // oklch(0.86 0.06 260)

  info:         c('--info',         '#3B82F6'),  // oklch(0.55 0.15 250)
  infoSoft:     c('--info-soft',    '#EFF6FF'),  // oklch(0.96 0.04 250)
  infoLine:     c('--info-line',    '#BFDBFE'),

  success:      c('--success',      '#16A34A'),  // oklch(0.50 0.14 150)
  successSoft:  c('--success-soft', '#F0FDF4'),  // oklch(0.95 0.05 150)
  successLine:  c('--success-line', '#BBF7D0'),

  warn:         c('--warn',         '#D97706'),  // oklch(0.62 0.13 60)
  warnSoft:     c('--warn-soft',    '#FFFBEB'),  // oklch(0.96 0.06 80)
  warnLine:     c('--warn-line',    '#FDE68A'),

  danger:       c('--danger',       '#DC2626'),  // oklch(0.55 0.13 25)
  dangerSoft:   c('--danger-soft',  '#FEF2F2'),  // oklch(0.96 0.04 25)
  dangerLine:   c('--danger-line',  '#FECACA'),

  neutral:      c('--neutral',      '#0891B2'),  // oklch(0.55 0.14 200)
  neutralSoft:  c('--neutral-soft', '#ECFEFF'),  // oklch(0.96 0.04 200)
  neutralLine:  c('--neutral-line', '#A5F3FC'),

  // INTERNAL space partition accent (Teal). Deliberately NEVER the customer
  // `accent` (indigo) — the two must never be confused. Mirrors the --internal*
  // CSS custom properties in theme.css.
  internal:     c('--internal',      '#1F8A9E'),  // oklch(0.52 0.12 215)
  internalInk:  c('--internal-ink',  '#176475'),  // oklch(0.40 0.11 215)
  internalSoft: c('--internal-soft', '#E8F6F8'),  // oklch(0.965 0.03 215)
  internalMute: c('--internal-mute', '#CDEAEF'),  // oklch(0.93 0.045 215)
  internalLine: c('--internal-line', '#9AD3DC'),  // oklch(0.84 0.07 215)

  white:        '#FFFFFF',
  black:        '#000000',
  transparent:  'transparent',
} as const;

// ─── Font Families ────────────────────────────────────────────────────────────
export const FontFamily = {
  sans: Platform.select({ web: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif", default: 'IBMPlexSans' }),
  mono: Platform.select({ web: "'IBM Plex Mono', ui-monospace, monospace", default: 'IBMPlexMono' }),
} as const;

// ─── Font Sizes ───────────────────────────────────────────────────────────────
export const FontSize = {
  xs:   11,
  sm:   12,
  md:   13,
  lg:   15,
  xl:   18,
  '2xl': 22,
  '3xl': 28,
} as const;

// ─── Font Weights ─────────────────────────────────────────────────────────────
export const FontWeight = {
  regular:  '400' as const,
  medium:   '500' as const,
  semibold: '600' as const,
  bold:     '700' as const,
};

// ─── Spacing ──────────────────────────────────────────────────────────────────
export const Space = {
  0:  0,
  1:  4,
  2:  8,
  3:  12,
  4:  16,
  5:  20,
  6:  24,
  8:  32,
  10: 40,
  12: 48,
  16: 64,
} as const;

// ─── Border Radii ─────────────────────────────────────────────────────────────
export const Radii = {
  r1:   3,
  r2:   5,
  r3:   8,
  r4:   12,
  r5:   16,
  full: 999,
} as const;

// ─── Shadows ──────────────────────────────────────────────────────────────────
export const Shadow = {
  sm: Platform.select({
    web: { boxShadow: 'var(--shadow-sm)' } as any,
    default: { shadowColor: '#101828', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 0, elevation: 1 },
  }),
  base: Platform.select({
    web: { boxShadow: 'var(--shadow)' } as any,
    default: { shadowColor: '#101828', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 2 },
  }),
  md: Platform.select({
    web: { boxShadow: 'var(--shadow-md)' } as any,
    default: { shadowColor: '#101828', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.10, shadowRadius: 12, elevation: 4 },
  }),
  lg: Platform.select({
    web: { boxShadow: 'var(--shadow-lg)' } as any,
    default: { shadowColor: '#101828', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.14, shadowRadius: 20, elevation: 8 },
  }),
} as const;

// ─── Layout Constants ─────────────────────────────────────────────────────────
export const Layout = {
  sidebarWidth:  232,
  topbarHeight:  50,
  rightPanelWidth: 300,
} as const;

// ─── Chip tone → color mapping ────────────────────────────────────────────────
export type ChipTone = 'info' | 'success' | 'warn' | 'danger' | 'neutral' | 'accent' | 'internal' | 'default';

export const ChipColors: Record<ChipTone, { color: string; bg: string; border: string; dot: string }> = {
  info:    { color: Colors.info,    bg: Colors.infoSoft,    border: Colors.infoLine,    dot: Colors.info },
  success: { color: Colors.success, bg: Colors.successSoft, border: Colors.successLine, dot: Colors.success },
  warn:    { color: Colors.warn,    bg: Colors.warnSoft,    border: Colors.warnLine,    dot: Colors.warn },
  danger:  { color: Colors.danger,  bg: Colors.dangerSoft,  border: Colors.dangerLine,  dot: Colors.danger },
  neutral: { color: Colors.neutral, bg: Colors.neutralSoft, border: Colors.neutralLine, dot: Colors.neutral },
  accent:  { color: Colors.accentInk, bg: Colors.accentSoft, border: Colors.accentLine, dot: Colors.accent },
  internal:{ color: Colors.internalInk, bg: Colors.internalSoft, border: Colors.internalLine, dot: Colors.internal },
  default: { color: Colors.ink2,    bg: Colors.card,        border: Colors.line,        dot: Colors.ink3 },
};
